<script setup>
import { ref, reactive, watch, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useRoute } from 'vue-router';
import Button from '../../../../../components-next/button/Button.vue';
import DynamicFieldList from './DynamicFieldList.vue';

const { t } = useI18n();
const route = useRoute();
const emit = defineEmits(['update', 'validation', 'show-info']);

// Verificar se estamos no modo de edição
const isEditMode = computed(() => route.query.mode === 'edit');

const props = defineProps({
  initialAdditionalInfo: {
    type: Array,
    default: () => []
  }
});

// Flags para controlar a animação dos botões
const isSupportButtonHovered = ref(false);

// Função para criar uma cópia profunda do objeto e validar estrutura
function deepClone(obj) {
  if (!Array.isArray(obj)) return [];

  return obj.map(item => {
    // Se é string, fazer parse (dados corrompidos do banco)
    if (typeof item === 'string') {
      try {
        let parsed = JSON.parse(item);
        // Se ainda for string após parse, tentar parsear novamente (duplo escape)
        if (typeof parsed === 'string') {
          try {
            parsed = JSON.parse(parsed);
          } catch {
            // Se falhar segundo parse, manter o primeiro parse
          }
        }
        // Se parseou com sucesso e é objeto, retornar com estrutura correta
        if (parsed && typeof parsed === 'object') {
          return {
            name: parsed.name !== undefined ? parsed.name : '',
            value: parsed.value !== undefined ? parsed.value : '',
            nameError: parsed.nameError || '',
            valueError: parsed.valueError || ''
          };
        }
        // Se parseou mas não é objeto, descartar
        return null;
      } catch {
        // Se não é JSON válido, descartar
        return null;
      }
    }
    // Se é objeto (incluindo objetos vazios durante edição), preservar
    if (item && typeof item === 'object') {
      return {
        name: item.name !== undefined ? item.name : '',
        value: item.value !== undefined ? item.value : '',
        nameError: item.nameError || '',
        valueError: item.valueError || ''
      };
    }
    // Outros tipos (null, undefined, number, etc), descartar
    return null;
  }).filter(item => item !== null);
}

// Inicializar additionalInfo com uma cópia completa dos dados iniciais
const additionalInfo = ref([]);

// Flags para evitar atualizações recursivas
const isUpdatingData = ref(false);
const isValidating = ref(false);

// Status de validação
const isValid = ref(true);

// Computed para verificar se o formulário inteiro é válido
const isFormValid = computed(() => {
  return isValid.value;
});

// Função para atualizar additionalInfo com dados iniciais
function updateAdditionalInfo() {
  isUpdatingData.value = true;
  
  additionalInfo.value = Array.isArray(props.initialAdditionalInfo) 
    ? deepClone(props.initialAdditionalInfo) 
    : [];
  
  // Usar setTimeout para quebrar o ciclo reativo
  setTimeout(() => {
    isUpdatingData.value = false;
  }, 0);
}

// Inicializar additionalInfo com os dados do props no mounted
onMounted(() => {
  updateAdditionalInfo();
  // Validar todos os campos diretamente
  validateAllFields();
});

// Observa mudanças e emite atualizações para o componente pai
watch(additionalInfo, () => {
  if (isUpdatingData.value) return; // Evita atualizações recursivas
  
  emit('update', deepClone(additionalInfo.value));
}, { deep: true });

// Quando o status de validação muda, emite evento de validação
watch(isValid, () => {
  if (isValidating.value) return; // Evita atualizações recursivas
  
  isValidating.value = true;
  emit('validation', isValid.value);
  // Usa setTimeout para quebrar o ciclo de atualização síncrona
  setTimeout(() => {
    isValidating.value = false;
  }, 0);
});

// Watch para props.initialAdditionalInfo
watch(() => props.initialAdditionalInfo, (newVal) => {
  
  // Usa setTimeout para garantir que a atualização e validação 
  // aconteçam em ciclos reativos separados
  setTimeout(() => {
    updateAdditionalInfo();
    
    // Outro setTimeout para garantir que a validação ocorra depois da atualização
    setTimeout(() => {
      validateAllFields();
    }, 0);
  }, 0);
}, { deep: true });

// Handler para o clique no botão de informação
const handleSupportInfoClick = () => {
  isSupportButtonHovered.value = true;
  useAlert('Adicione informações adicionais e outros detalhes que seu agente deve conhecer para prestar um atendimento eficaz.', { type: 'info', duration: 7000 });
};

// Função para adicionar campos de suporte com verificação inicial
const addSupportField = () => {
  additionalInfo.value.unshift({
    name: '',
    value: '',
    nameError: '',
    valueError: ''
  });
  
  // Quando adiciona um novo campo, marca que há campos adicionais que precisam ser validados
  isValid.value = false;
  
  // Emite o estado de validação atualizado
  emit('validation', isValid.value);
};

// Função para atualizar informações adicionais
const updateAdditionalInfoList = (newAdditionalInfo) => {
  additionalInfo.value = newAdditionalInfo;
  
  // Se não houver informações adicionais, considera válido
  if (!newAdditionalInfo || newAdditionalInfo.length === 0) {
    isValid.value = true;
  }
  
  // Emite o estado de validação atualizado
  emit('validation', isValid.value);
};

// Função auxiliar para validar os campos dinâmicos
const validateAdditionalInfo = () => {
  const hasEmptyFields = additionalInfo.value.some(
    item => !item.name || !item.name.trim() || !item.value || !item.value.trim()
  );
  isValid.value = !hasEmptyFields;
  return !hasEmptyFields;
};

// Função para validar todos os campos
const validateAllFields = () => {
  isValidating.value = true;
  
  if (additionalInfo.value.length > 0) {
    validateAdditionalInfo();
  } else {
    isValid.value = true;
  }
  
  setTimeout(() => {
    emit('validation', isValid.value);
    isValidating.value = false;
  }, 0);
};

// Handler para o evento de validação do componente filho
const handleAdditionalInfoValidation = (valid) => {
  isValid.value = valid;
  if (additionalInfo.value.length === 0) {
    isValid.value = true;
  }
};
</script>

<template>
  <div class="space-y-6">
    <div class="space-y-4">
      <!-- Cabeçalho com título e botões -->
      <div class="flex items-center justify-between">
        <h3 class="text-base font-medium text-n-slate-12 flex items-center">
          <span class="i-lucide-file-text mr-2"></span>
          Informações Complementares
          <!-- Botão de informação -->
          <button
            @click="$emit('show-info')"
            class="ml-2 p-1 rounded-full hover:bg-n-slate-3 transition-colors"
            title="Informações sobre dados complementares"
          >
            <svg class="w-4 h-4 text-n-slate-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </button>
        </h3>
        <Button
          label="Adicionar Informações"
          color="blue"
          variant="outline"
          icon="plus"
          size="sm"
          @click="addSupportField"
        />
      </div>

      <!-- Lista de campos -->
      <DynamicFieldList
        v-if="additionalInfo.length > 0"
        :items="additionalInfo"
        name-label="Nome do Campo"
        name-placeholder="Nome do Campo"
        value-label="Valor do Campo"
        value-placeholder="Valor do Campo"
        item-type="field"
        @update="updateAdditionalInfoList"
        @validation="handleAdditionalInfoValidation"
      />

      <!-- Mensagem quando não há campos -->
      <div v-else class="text-sm text-n-slate-11 italic bg-n-slate-2 dark:bg-n-slate-12/5 rounded-lg p-4 border border-n-slate-5 dark:border-n-slate-10/20">
        <div class="flex items-center gap-2">
          <span class="i-lucide-info w-4 h-4 text-n-slate-9"></span>
          <span>
            Nenhuma informação complementar definida. Clique em "Adicionar Informações" para incluir detalhes extras sobre produtos, serviços ou procedimentos.
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Animação de pulso para os botões de informação */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(246, 148, 157, 0.7);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 2px 5px rgba(246, 148, 157, 0);
    transform: scale(1.1);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(246, 148, 157, 0);
    transform: scale(1);
  }
}

.pulse-animation {
  animation: pulse 2s infinite;
}

.info-btn {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  line-height: 1 !important;
}
</style>